// Loyalty admin page (slice 30, ADR 0028). Owner edits the per-
// restaurant rules; staff looks up a customer by phone + earns or
// redeems. Permission gating hides owner-only controls (configure,
// adjust) from staff; the server stays authoritative via
// PermissionGuard on every write.
//
// URL shape: /orgs/:orgId/restaurants/:restaurantId/loyalty
//   ?outletId=<default outlet to pre-select>
//
// The optional outletId query param is a convenience from the home
// page's row (one restaurant may own several outlets; the home page
// link carries the outlet the user clicked from, pre-selecting it in
// the earn/redeem forms).

import { useEffect, useMemo, useState, type FormEvent, type JSX } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../api/client.js';
import type {
  LoyaltyAccountResponse,
  LoyaltyAdjustBody,
  LoyaltyEarnBody,
  LoyaltyLedgerResponse,
  LoyaltyLookupBody,
  LoyaltyLookupResponse,
  LoyaltyMutationResponse,
  LoyaltyRedeemBody,
  LoyaltyRules,
  MeResponse,
  UpdateLoyaltyRulesBody,
} from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { hasPermission, useOrgPermissions } from '../auth/permissions.js';

export function LoyaltyPage(): JSX.Element {
  const { orgId, restaurantId } = useParams<{ orgId: string; restaurantId: string }>();
  const [search] = useSearchParams();
  const defaultOutletId = search.get('outletId') ?? '';
  const { user } = useAuth();
  const qc = useQueryClient();

  // Keep the me cache warm so useOrgPermissions resolves on refresh.
  useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<MeResponse>('/me'),
    staleTime: 30_000,
  });

  const basePath = `/organizations/${orgId}/restaurants/${restaurantId}/loyalty`;
  const perms = useOrgPermissions(orgId);
  const canConfigure = hasPermission(perms, 'loyalty:configure');
  const canAdjust = hasPermission(perms, 'loyalty:adjust');
  const canEarn = hasPermission(perms, 'loyalty:earn');
  const canRedeem = hasPermission(perms, 'loyalty:redeem');

  // The me cache also carries the user's outlets so the page can
  // offer a drop-down when no outletId was passed as a query param.
  const me = qc.getQueryData<MeResponse>(['me']);
  const outletsInRestaurant = useMemo(
    () =>
      (me?.memberships ?? [])
        .filter((m) => m.organization.id === orgId && m.restaurant.id === restaurantId)
        .map((m) => m.outlet),
    [me, orgId, restaurantId],
  );

  // ---------------- rules ----------------
  const rulesKey = ['loyalty-rules', orgId, restaurantId] as const;
  const rules = useQuery({
    queryKey: rulesKey,
    queryFn: () => apiFetch<LoyaltyRules>(`${basePath}/rules`),
    enabled: !!orgId && !!restaurantId,
  });

  // ---------------- customer lookup ----------------
  const [lookupPhone, setLookupPhone] = useState('');
  const [lookupOutlet, setLookupOutlet] = useState(defaultOutletId);
  const [lookupDisplayName, setLookupDisplayName] = useState('');
  const [resolvedCustomer, setResolvedCustomer] = useState<LoyaltyLookupResponse | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  useEffect(() => {
    if (!lookupOutlet && outletsInRestaurant.length > 0) {
      setLookupOutlet(outletsInRestaurant[0]!.id);
    }
  }, [lookupOutlet, outletsInRestaurant]);

  const lookup = useMutation({
    mutationFn: (body: LoyaltyLookupBody) =>
      apiFetch<LoyaltyLookupResponse>(`${basePath}/customers/lookup`, { method: 'POST', body }),
    onSuccess: (data) => {
      setResolvedCustomer(data);
      setLookupError(null);
    },
    onError: (err) => {
      setResolvedCustomer(null);
      setLookupError(apiErrorMessage(err));
    },
  });

  function onLookup(e: FormEvent): void {
    e.preventDefault();
    const phone = lookupPhone.trim();
    const outletId = lookupOutlet.trim();
    if (!phone || !outletId) {
      setLookupError('Enter a phone number and pick an outlet.');
      return;
    }
    const body: LoyaltyLookupBody = {
      phoneE164: phone,
      outletId,
      ...(lookupDisplayName.trim() ? { displayName: lookupDisplayName.trim() } : {}),
    };
    lookup.mutate(body);
  }

  // ---------------- account + ledger ----------------
  const customerId = resolvedCustomer?.customerId;
  const accountKey = ['loyalty-account', orgId, restaurantId, customerId] as const;
  const account = useQuery({
    queryKey: accountKey,
    queryFn: () => apiFetch<LoyaltyAccountResponse>(`${basePath}/accounts/${customerId}`),
    enabled: !!customerId,
  });
  const ledgerKey = ['loyalty-ledger', orgId, restaurantId, customerId] as const;
  const ledger = useQuery({
    queryKey: ledgerKey,
    queryFn: () => apiFetch<LoyaltyLedgerResponse>(`${basePath}/accounts/${customerId}/ledger`),
    enabled: !!customerId,
  });

  function refreshAccount(): void {
    qc.invalidateQueries({ queryKey: accountKey });
    qc.invalidateQueries({ queryKey: ledgerKey });
  }

  // ---------------- earn / redeem / adjust ----------------
  const earn = useMutation({
    mutationFn: (body: LoyaltyEarnBody) =>
      apiFetch<LoyaltyMutationResponse>(`${basePath}/accounts/${customerId}/earn`, {
        method: 'POST',
        body,
      }),
    onSuccess: refreshAccount,
  });
  const redeem = useMutation({
    mutationFn: (body: LoyaltyRedeemBody) =>
      apiFetch<LoyaltyMutationResponse>(`${basePath}/accounts/${customerId}/redeem`, {
        method: 'POST',
        body,
      }),
    onSuccess: refreshAccount,
  });
  const adjust = useMutation({
    mutationFn: (body: LoyaltyAdjustBody) =>
      apiFetch<LoyaltyMutationResponse>(`${basePath}/accounts/${customerId}/adjust`, {
        method: 'POST',
        body,
      }),
    onSuccess: refreshAccount,
  });

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>Loyalty</h1>
          <p className="muted">
            Signed in as <strong>{user?.email}</strong> · <Link to="/home">Back to outlets</Link>
          </p>
        </div>
      </header>

      {/* --------------- rules --------------- */}
      <section className="card loyalty-rules">
        <h2>Program rules</h2>
        {rules.isLoading ? <p>Loading…</p> : null}
        {rules.error && !rules.data ? <p className="error">Could not load rules.</p> : null}
        {rules.data ? (
          <RulesForm
            basePath={basePath}
            rulesKey={rulesKey}
            canConfigure={canConfigure}
            initial={rules.data}
          />
        ) : null}
      </section>

      {/* --------------- customer lookup --------------- */}
      <section className="card loyalty-lookup">
        <h2>Find or create customer</h2>
        <form onSubmit={onLookup} className="loyalty-form">
          <label htmlFor="phone">Phone number</label>
          <input
            id="phone"
            type="tel"
            inputMode="tel"
            placeholder="+91 98765 43210"
            value={lookupPhone}
            onChange={(e) => setLookupPhone(e.target.value)}
          />
          <label htmlFor="displayName">Name (optional)</label>
          <input
            id="displayName"
            type="text"
            value={lookupDisplayName}
            onChange={(e) => setLookupDisplayName(e.target.value)}
          />
          <label htmlFor="lookupOutlet">Outlet</label>
          <select
            id="lookupOutlet"
            value={lookupOutlet}
            onChange={(e) => setLookupOutlet(e.target.value)}
          >
            <option value="">— pick outlet —</option>
            {outletsInRestaurant.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
          <div className="row-actions">
            <button type="submit" disabled={lookup.isPending}>
              {lookup.isPending ? 'Looking up…' : 'Find customer'}
            </button>
          </div>
          {lookupError ? (
            <p className="error" role="alert">
              {lookupError}
            </p>
          ) : null}
        </form>
      </section>

      {/* --------------- balance + actions + ledger --------------- */}
      {resolvedCustomer ? (
        <>
          <section className="card loyalty-balance">
            <h2>Balance</h2>
            <p className="muted">
              Customer <strong>{resolvedCustomer.phoneE164Masked}</strong>
              {resolvedCustomer.displayName ? ` · ${resolvedCustomer.displayName}` : ''}
            </p>
            {account.data?.account ? (
              <ul className="loyalty-counters">
                <li>
                  <strong>{account.data.account.balancePoints}</strong>
                  <span>points</span>
                </li>
                <li>
                  <strong>{account.data.account.lifetimeEarned}</strong>
                  <span>lifetime earned</span>
                </li>
                <li>
                  <strong>{account.data.account.lifetimeRedeemed}</strong>
                  <span>lifetime redeemed</span>
                </li>
              </ul>
            ) : (
              <p className="muted">No activity yet.</p>
            )}
          </section>

          <section className="card loyalty-actions">
            <h2>Record a transaction</h2>
            <div className="loyalty-action-grid">
              {canEarn ? (
                <EarnForm
                  outlets={outletsInRestaurant}
                  defaultOutletId={defaultOutletId || lookupOutlet}
                  onSubmit={(body) => earn.mutate(body)}
                  pending={earn.isPending}
                  error={earn.error}
                />
              ) : null}
              {canRedeem ? (
                <RedeemForm
                  outlets={outletsInRestaurant}
                  defaultOutletId={defaultOutletId || lookupOutlet}
                  onSubmit={(body) => redeem.mutate(body)}
                  pending={redeem.isPending}
                  error={redeem.error}
                />
              ) : null}
              {canAdjust ? (
                <AdjustForm
                  onSubmit={(body) => adjust.mutate(body)}
                  pending={adjust.isPending}
                  error={adjust.error}
                />
              ) : null}
            </div>
          </section>

          <section className="card loyalty-ledger">
            <h2>Recent activity</h2>
            {ledger.isLoading ? <p>Loading…</p> : null}
            {ledger.data && ledger.data.entries.length === 0 ? (
              <p className="muted">No ledger entries yet.</p>
            ) : null}
            {ledger.data && ledger.data.entries.length > 0 ? (
              <ul className="ledger-list">
                {ledger.data.entries.map((e) => (
                  <li key={e.id} className={`ledger-row ledger-${e.entryType}`}>
                    <span className="ledger-type">{e.entryType}</span>
                    <span className="ledger-points">
                      {e.points > 0 ? `+${e.points}` : e.points}
                    </span>
                    <span className="ledger-balance">balance {e.balanceAfter}</span>
                    <span className="ledger-note muted">
                      {e.billReference ? `bill ${e.billReference}` : null}
                      {e.note ? ` · ${e.note}` : null}
                    </span>
                    <span className="ledger-date muted">
                      {new Date(e.createdAt).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        </>
      ) : null}
    </main>
  );
}

// ---------------- rules form ----------------

interface RulesFormProps {
  basePath: string;
  rulesKey: readonly unknown[];
  canConfigure: boolean;
  initial: LoyaltyRules;
}

function RulesForm({ basePath, rulesKey, canConfigure, initial }: RulesFormProps): JSX.Element {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(initial.enabled);
  const [earnPointsPerHundred, setEarnPointsPerHundred] = useState(initial.earnPointsPerHundred);
  const [maxRedeemBps, setMaxRedeemBps] = useState(initial.maxRedeemFractionBps);
  const [minBillPaise, setMinBillPaise] = useState(initial.minBillPaise);
  const [minRedeem, setMinRedeem] = useState(initial.minRedeemPoints);

  useEffect(() => {
    setEnabled(initial.enabled);
    setEarnPointsPerHundred(initial.earnPointsPerHundred);
    setMaxRedeemBps(initial.maxRedeemFractionBps);
    setMinBillPaise(initial.minBillPaise);
    setMinRedeem(initial.minRedeemPoints);
  }, [initial]);

  const save = useMutation({
    mutationFn: (body: UpdateLoyaltyRulesBody) =>
      apiFetch<LoyaltyRules>(`${basePath}/rules`, { method: 'PUT', body }),
    onSuccess: (data) => {
      qc.setQueryData(rulesKey, data);
    },
  });

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    save.mutate({
      enabled,
      earnPointsPerHundred,
      maxRedeemFractionBps: maxRedeemBps,
      minBillPaise,
      minRedeemPoints: minRedeem,
      expectedVersion: initial.version,
    });
  }

  const errorMsg = apiErrorMessage(save.error);

  return (
    <form onSubmit={onSubmit} className="loyalty-form">
      <label>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          disabled={!canConfigure || save.isPending}
        />{' '}
        Loyalty enabled
      </label>
      <label htmlFor="earnPpH">Earn points per ₹100</label>
      <input
        id="earnPpH"
        type="number"
        min={0}
        max={10000}
        value={earnPointsPerHundred}
        onChange={(e) => setEarnPointsPerHundred(Number(e.target.value))}
        disabled={!canConfigure || save.isPending}
      />
      <label htmlFor="maxRedeemBps">Max redeem % of bill (basis points, 10000 = 100%)</label>
      <input
        id="maxRedeemBps"
        type="number"
        min={0}
        max={10000}
        value={maxRedeemBps}
        onChange={(e) => setMaxRedeemBps(Number(e.target.value))}
        disabled={!canConfigure || save.isPending}
      />
      <label htmlFor="minBillPaise">Minimum bill (paise)</label>
      <input
        id="minBillPaise"
        type="number"
        min={0}
        value={minBillPaise}
        onChange={(e) => setMinBillPaise(Number(e.target.value))}
        disabled={!canConfigure || save.isPending}
      />
      <label htmlFor="minRedeem">Minimum redeem points</label>
      <input
        id="minRedeem"
        type="number"
        min={0}
        value={minRedeem}
        onChange={(e) => setMinRedeem(Number(e.target.value))}
        disabled={!canConfigure || save.isPending}
      />
      <div className="row-actions">
        <button type="submit" disabled={!canConfigure || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save rules'}
        </button>
      </div>
      {errorMsg ? (
        <p className="error" role="alert">
          {errorMsg}
        </p>
      ) : null}
      {!canConfigure ? <p className="muted">You do not have permission to edit rules.</p> : null}
    </form>
  );
}

// ---------------- earn / redeem / adjust forms ----------------

interface OutletOption {
  readonly id: string;
  readonly name: string;
}

interface EarnFormProps {
  outlets: readonly OutletOption[];
  defaultOutletId: string;
  onSubmit: (body: LoyaltyEarnBody) => void;
  pending: boolean;
  error: unknown;
}

function EarnForm({
  outlets,
  defaultOutletId,
  onSubmit,
  pending,
  error,
}: EarnFormProps): JSX.Element {
  const [billRef, setBillRef] = useState('');
  const [billRupees, setBillRupees] = useState('');
  const [outletId, setOutletId] = useState(defaultOutletId);

  useEffect(() => {
    if (!outletId && outlets.length > 0) setOutletId(outlets[0]!.id);
  }, [outletId, outlets]);

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    const paise = Math.round(Number(billRupees) * 100);
    if (!billRef.trim() || !outletId || !Number.isFinite(paise) || paise <= 0) return;
    onSubmit({ billReference: billRef.trim(), billAmountPaise: paise, outletId });
  }

  return (
    <form onSubmit={handleSubmit} className="loyalty-form">
      <h3>Earn</h3>
      <label htmlFor="earnBillRef">Bill reference</label>
      <input
        id="earnBillRef"
        type="text"
        value={billRef}
        onChange={(e) => setBillRef(e.target.value)}
      />
      <label htmlFor="earnBillAmount">Bill amount (₹)</label>
      <input
        id="earnBillAmount"
        type="number"
        min={1}
        step="0.01"
        value={billRupees}
        onChange={(e) => setBillRupees(e.target.value)}
      />
      <label htmlFor="earnOutlet">Outlet</label>
      <select id="earnOutlet" value={outletId} onChange={(e) => setOutletId(e.target.value)}>
        {outlets.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
      <button type="submit" disabled={pending}>
        {pending ? 'Saving…' : 'Credit points'}
      </button>
      {error ? (
        <p className="error" role="alert">
          {apiErrorMessage(error)}
        </p>
      ) : null}
    </form>
  );
}

interface RedeemFormProps {
  outlets: readonly OutletOption[];
  defaultOutletId: string;
  onSubmit: (body: LoyaltyRedeemBody) => void;
  pending: boolean;
  error: unknown;
}

function RedeemForm({
  outlets,
  defaultOutletId,
  onSubmit,
  pending,
  error,
}: RedeemFormProps): JSX.Element {
  const [points, setPoints] = useState('');
  const [billRupees, setBillRupees] = useState('');
  const [outletId, setOutletId] = useState(defaultOutletId);

  useEffect(() => {
    if (!outletId && outlets.length > 0) setOutletId(outlets[0]!.id);
  }, [outletId, outlets]);

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    const p = Number(points);
    const paise = Math.round(Number(billRupees) * 100);
    if (!Number.isFinite(p) || p <= 0 || !Number.isFinite(paise) || paise <= 0 || !outletId) return;
    onSubmit({ points: p, billAmountPaise: paise, outletId });
  }

  return (
    <form onSubmit={handleSubmit} className="loyalty-form">
      <h3>Redeem</h3>
      <label htmlFor="redeemPoints">Points to redeem</label>
      <input
        id="redeemPoints"
        type="number"
        min={1}
        value={points}
        onChange={(e) => setPoints(e.target.value)}
      />
      <label htmlFor="redeemBillAmount">Bill amount (₹)</label>
      <input
        id="redeemBillAmount"
        type="number"
        min={1}
        step="0.01"
        value={billRupees}
        onChange={(e) => setBillRupees(e.target.value)}
      />
      <label htmlFor="redeemOutlet">Outlet</label>
      <select id="redeemOutlet" value={outletId} onChange={(e) => setOutletId(e.target.value)}>
        {outlets.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
      <button type="submit" disabled={pending}>
        {pending ? 'Saving…' : 'Debit points'}
      </button>
      {error ? (
        <p className="error" role="alert">
          {apiErrorMessage(error)}
        </p>
      ) : null}
    </form>
  );
}

interface AdjustFormProps {
  onSubmit: (body: LoyaltyAdjustBody) => void;
  pending: boolean;
  error: unknown;
}

function AdjustForm({ onSubmit, pending, error }: AdjustFormProps): JSX.Element {
  const [points, setPoints] = useState('');
  const [note, setNote] = useState('');

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    const p = Number(points);
    if (!Number.isFinite(p) || p === 0 || !note.trim()) return;
    onSubmit({ points: p, note: note.trim() });
  }

  return (
    <form onSubmit={handleSubmit} className="loyalty-form">
      <h3>Adjust (owner only)</h3>
      <label htmlFor="adjustPoints">Points (± to add or remove)</label>
      <input
        id="adjustPoints"
        type="number"
        value={points}
        onChange={(e) => setPoints(e.target.value)}
      />
      <label htmlFor="adjustNote">Note</label>
      <input id="adjustNote" type="text" value={note} onChange={(e) => setNote(e.target.value)} />
      <button type="submit" disabled={pending}>
        {pending ? 'Saving…' : 'Record adjustment'}
      </button>
      {error ? (
        <p className="error" role="alert">
          {apiErrorMessage(error)}
        </p>
      ) : null}
    </form>
  );
}

// ---------------- helpers ----------------

function apiErrorMessage(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof ApiError) {
    if (error.status === 400) {
      const body = error.body as
        | { message?: { code?: string; message?: string }; details?: { message: string }[] }
        | undefined;
      const code = body?.message?.code;
      if (code === 'invalid_phone') return 'That phone number does not look right.';
      if (code === 'bill_below_minimum') return 'Bill is below the minimum needed to earn points.';
      if (code === 'no_points_earned') return 'Bill is too small to earn any points.';
      if (code === 'insufficient_balance') return 'Not enough points on this account.';
      if (code === 'below_min_redeem') return 'Below the minimum redeemable points.';
      if (code === 'redeem_exceeds_cap') return 'Exceeds the maximum redeem fraction of the bill.';
      const detail = body?.details?.[0]?.message;
      return detail ?? body?.message?.message ?? 'Request was rejected.';
    }
    if (error.status === 403) return 'You do not have permission to do that.';
    if (error.status === 404) return 'Not found.';
    if (error.status === 409) {
      const body = error.body as { message?: { code?: string } } | undefined;
      if (body?.message?.code === 'loyalty_disabled') return 'Loyalty is not enabled yet.';
      if (body?.message?.code === 'bill_already_credited')
        return 'This bill already earned points.';
      if (body?.message?.code === 'version_conflict')
        return 'Someone else edited this — reload and try again.';
      return 'Conflict with existing data.';
    }
    if (error.status === 0) return 'Network error — check your connection.';
    return 'Could not complete that action.';
  }
  return 'Could not complete that action.';
}
